import React, { useState, useEffect } from 'react';
import { X, Play, Clock, Check, Eye, EyeOff, ArrowRight, Crown, List  } from 'lucide-react';
import API_URL from '../config/API_URL';

export const ViewChildStoriesModal = ({ isOpen, onClose, childId, childName, queuedStories = [] }) => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'queued'

  useEffect(() => {
    if (isOpen && childId) {
      fetchChildRecordings();
    }
  }, [isOpen, childId]);

  const fetchChildRecordings = async () => {
    setLoading(true);
    try {
      // Fetch regular recordings
      const response = await fetch(`${API_URL}/api/recording/kid_fromGparent/${childId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch recordings');
      
      const data = await response.json();
      
      // Fetch personal recordings assigned to this kid
      const personalResponse = await fetch(`${API_URL}/api/personal-books/kid/${childId}/recordings`, {
        credentials: 'include'
      });
      
      let allRecordings = data.recordings || [];
      
      if (personalResponse.ok) {
        const personalData = await personalResponse.json();
        // Transform personal recordings to match expected format
        const personalRecordings = personalData.map(rec => ({
          ...rec,
          id: `personal_${rec.id}`,
          is_personal: true,
          is_premium: false,
          book: {
            title: rec.book?.title || 'Untitled Book',
            author: rec.book?.author || 'Unknown',
            cover_image_url: rec.book?.cover_image_url
          },
          reader: rec.reader
        }));
        
        allRecordings = [...allRecordings, ...personalRecordings];
      }
      
      setRecordings(allRecordings);
    } catch (error) {
      console.error('Error fetching child recordings:', error);
      alert('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  // Filter queued stories for this specific child
  const childQueuedStories = queuedStories.filter(story => story.kid_id === childId);



  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        padding: '2rem',
        maxWidth: '700px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
            {childName}'s Stories
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '1.5rem',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0.5rem'
        }}>
          <button
            onClick={() => setActiveTab('current')}
            style={{
              padding: '0.5rem 1rem',
              background: activeTab === 'current' ? '#9333ea' : 'transparent',
              color: activeTab === 'current' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Play size={16} />
            Current Stories ({recordings.length})
          </button>
          <button
            onClick={() => setActiveTab('queued')}
            style={{
              padding: '0.5rem 1rem',
              background: activeTab === 'queued' ? '#9333ea' : 'transparent',
              color: activeTab === 'queued' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <List size={16} />
            Queued Stories ({childQueuedStories.length})
          </button>
        </div>

        {loading ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>Loading stories...</p>
        ) : (
          <>
            {/* Current Stories Tab */}
            {activeTab === 'current' && (
              recordings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>No stories assigned yet</p>
                  <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    Click "Add Story" to assign a recording to {childName}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {recordings.map(recording => {
                    const isPersonal = recording.is_personal;
                    
                    return (
                      <div
                        key={recording.id}
                        style={{
                          border: isPersonal 
                            ? '2px solid #10b981'
                            : recording.is_premium 
                            ? '2px solid #fbbf24' 
                            : '1px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          padding: '1.25rem',
                          background: isPersonal
                            ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                            : recording.is_premium 
                            ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
                            : '#fafafa',
                          transition: 'all 0.2s',
                          position: 'relative'
                        }}
                      >
                        {/* Personal Badge */}
                        {isPersonal && (
                          <div style={{
                            position: 'absolute',
                            top: '1rem',
                            right: '1rem',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            📚 PERSONAL
                          </div>
                        )}

                        {/* Premium Badge */}
                        {recording.is_premium && !isPersonal && (
                          <div style={{
                            position: 'absolute',
                            top: '1rem',
                            right: '1rem',
                            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            <Crown size={10} />
                            PREMIUM
                          </div>
                        )}

                        <div style={{ 
                          display: 'flex', 
                          gap: '1rem', 
                          alignItems: 'flex-start', 
                          paddingRight: (recording.is_premium || isPersonal) ? '4rem' : '0' 
                        }}>
                          {/* Show cover image for personal books */}
                          {isPersonal && recording.book?.cover_image_url && (
                            <img 
                              src={recording.book.cover_image_url}
                              alt={recording.book?.title}
                              style={{
                                width: '60px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '0.375rem',
                                border: '2px solid #10b981',
                                flexShrink: 0
                              }}
                            />
                          )}
                          
                          <div style={{ flex: 1 }}>
                            <h4 style={{ 
                              fontWeight: '600', 
                              fontSize: '1.125rem', 
                              marginBottom: '0.25rem', 
                              color: isPersonal 
                                ? '#064e3b'
                                : recording.is_premium 
                                ? '#92400e' 
                                : '#1f2937'
                            }}>
                              {recording.book?.title || 'Unknown Book'}
                            </h4>
                            <p style={{ 
                              fontSize: '0.875rem', 
                              color: isPersonal
                                ? '#047857'
                                : recording.is_premium 
                                ? '#92400e' 
                                : '#6b7280',
                              marginBottom: '0.5rem' 
                            }}>
                              by {recording.book?.author || 'Unknown'}
                            </p>
                            <div style={{ 
                              display: 'flex', 
                              gap: '1rem', 
                              fontSize: '0.75rem', 
                              color: isPersonal
                                ? '#065f46'
                                : recording.is_premium 
                                ? '#78350f' 
                                : '#9ca3af',
                              marginTop: '0.75rem',
                              flexWrap: 'wrap'
                            }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <Clock size={14} />
                                {Math.floor(recording.duration_seconds / 60)}:{(recording.duration_seconds % 60).toString().padStart(2, '0')}
                              </span>
                              <span>
                                Read by {recording.reader?.first_name} {recording.reader?.last_name}
                              </span>
                              {isPersonal && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  {recording.recording_type === 'audio_only' ? '🎙️' : '📹'}
                                  {recording.recording_type === 'audio_only' ? 'Audio' : 'Video'}
                                </span>
                              )}
                            </div>
                            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                              <span style={{ 
                                color: isPersonal
                                  ? '#065f46'
                                  : recording.is_premium 
                                  ? '#78350f' 
                                  : '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}>
                                <EyeOff size={14} />
                                Not watched yet
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              // Handle personal recording URLs differently
                              if (isPersonal) {
                                const actualId = recording.id.toString().replace('personal_', '');
                                window.location.href = `/personal-recording/${actualId}`;
                              } else {
                                window.location.href = `/recording/${recording.id}`;
                              }
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              background: isPersonal
                                ? 'linear-gradient(135deg, #10b981, #059669)'
                                : recording.is_premium
                                ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                                : '#9333ea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            View <ArrowRight size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* Queued Stories Tab */}
            {activeTab === 'queued' && (
              childQueuedStories.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>No stories in queue</p>
                  <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    Stories requested for {childName} will appear here
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {childQueuedStories.map(story => (
                    <div
                      key={story.id}
                      style={{
                        border: story.is_premium ? '2px solid #fbbf24' : '1px solid #e5e7eb',
                        borderRadius: '0.75rem',
                        padding: '1.25rem',
                        background: story.is_premium 
                          ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
                          : '#fafafa',
                        transition: 'all 0.2s',
                        position: 'relative'
                      }}
                    >
                      {story.is_premium && (
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.625rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <Crown size={10} />
                          PREMIUM
                        </div>
                      )}

                      {/* Queued Badge */}
                      <div style={{
                        position: 'absolute',
                        top: story.is_premium ? '2.5rem' : '1rem',
                        right: '1rem',
                        background: '#f59e0b',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        <List size={10} />
                        QUEUED
                      </div>

                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', paddingRight: '4rem' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ 
                            fontWeight: '600', 
                            fontSize: '1.125rem', 
                            marginBottom: '0.25rem', 
                            color: story.is_premium ? '#92400e' : '#1f2937'
                          }}>
                            {story.book?.title || 'Unknown Book'}
                          </h4>
                          <p style={{ 
                            fontSize: '0.875rem', 
                            color: story.is_premium ? '#92400e' : '#6b7280',
                            marginBottom: '0.5rem' 
                          }}>
                            by {story.book?.author || 'Unknown'}
                          </p>
                          {story.book?.description && (
                            <p style={{ 
                              fontSize: '0.75rem', 
                              color: story.is_premium ? '#78350f' : '#9ca3af',
                              marginTop: '0.5rem',
                              fontStyle: 'italic'
                            }}>
                              {story.book.description}
                            </p>
                          )}
                          <div style={{ 
                            marginTop: '0.75rem',
                            fontSize: '0.75rem',
                            color: story.is_premium ? '#78350f' : '#6b7280'
                          }}>
                            <p>Requested by: {story.requested_by?.name || 'Unknown'}</p>
                            {story.is_premium && story.book?.token_cost && (
                              <p style={{ marginTop: '0.25rem' }}>
                                Token cost: {story.book.token_cost} token{story.book.token_cost !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const url = story.is_premium 
                              ? `/book/${story.book_id}?premium=true&childId=${childId}`
                              : `/book/${story.book_id}?childId=${childId}`;
                            window.location.href = url;
                            window.location.href = url;
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: story.is_premium
                              ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                              : '#9333ea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          View <ArrowRight size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}